import { FormInputs as CopyFormInputs } from "../components/CreateStore/CreateStoreModalContent/CreateStoreModalContent";

export const APITYPE = {
  Cost: "/rpcost-swa/V1",
  Items: "/rpitems-swa/V1",
  Stores: "/rpstores-swa/V1",
  Suppliers: "/rpsuppliers-swa/V1",
};

export const BASE_API_URL = import.meta.env.VITE_BASE_API_URL as string;
// Per-API base URL overrides. When set, they point at the new b2b APIM
// gateway (e.g. https://ge-dev-api.b2b.gianteagle.com/merch/rp/items-swa/V1).
// When unset, fall back to the legacy `${BASE_API_URL}${APITYPE.X}` pattern,
// which works for local dev (vite proxy rewrites `/rp{api}-swa/V1` to
// `/merch/rp/{api}-swa/V1` against localhost APIs).
export const ITEMS_BASE_API_URL =
  (import.meta.env.VITE_ITEMS_API_URL as string) ||
  `${BASE_API_URL}${APITYPE.Items}`;
export const STORES_BASE_API_URL =
  (import.meta.env.VITE_STORES_API_URL as string) ||
  `${BASE_API_URL}${APITYPE.Stores}`;
export const SUPPLIERS_BASE_API_URL =
  (import.meta.env.VITE_SUPPLIERS_API_URL as string) ||
  `${BASE_API_URL}${APITYPE.Suppliers}`;
export const COST_BASE_API_URL =
  (import.meta.env.VITE_COST_API_URL as string) ||
  `${BASE_API_URL}${APITYPE.Cost}`;
// PROMOTIONS_BASE_API_URL retired — its 7 endpoints now live on the Items
// API (BE PR #280). The VITE_PROMOTIONS_BASE_API_URL workflow var is left
// in place for now in case any consumer still reads it, but the FE no
// longer does.

export const TABLE_HEIGHT_OFFSET = "70px";

export const PAGETITLES = {
  BatchStatus: "Batch Status",
  BatchUpload: "Batch Upload",
  Stores: "Stores",
  ZoneBanners: "Zone Banners",
  CopyStoreDSD: "Copy Store DSD",
  CopyStoreZones: "Copy Store Zones",
  Supplier: "Suppliers List",
  Items: "Items",
  Admin: "Admin",
  ItemState: "Item-State Flag",
  PseudoUpc: "Next Warehouse/NEI/DSD Pseudo UPC",
  PriceGroupMove: "Price Group Moves",
  StateSubgroups: "State Subgroups",
  ItemAuthorization: "Item Authorizations",
  MaintainStoreCoupons: "Maintain Store Coupons",
  MiscellaneousTransactionCodes: "Miscellaneous Transaction Codes",
  ShipperCost: "Item/Shipper Cost",
  DealCost: "Deal Cost",
  DSDCostLink: "DSD Cost Link",
  DisplayCostDetails: "Display Cost Details",
  ItemMaintenance: "Item Maintenance",
  Promotions: "Add/Maintain Reward Promotions",
  SecurityManagement: "QA Access",
  PromotionDetail: "Reward Promotion Details",
  PromotionCreate: "Create Reward Promotion",
  PromotionEdit: "Edit Reward Promotion",
  StoreSuppliers: "Suppliers for a Store",
  StoreZones: "Zones for a Store",
  StoreDeptXrefs: "Store Department Cross Reference",
  TagHandlingInstructions: "Tag Handling Instructions",
  TagReplacementStoreRequest: "Request Replacement Tags - Store",
  TagFullScanShelfTagRequest: "Request Store Full-Scan Shelf Tags",
  Pricing: "Pricing",
  DisplayPriceDetails: "Display Price Details",
  FamilyMaint: "Family Maint",
  ScaleDepartments: "Scale Departments",
  ScaleTareCodes: "Scale Tare Codes",
  CaseInterpackItem: "Case Interpak Items",
  PosFileCompare: "POS File Compare",
  Journal: "Journal Viewer",
  ValidationList: "Validation List",
  PromotionTypesRules: "Promotion Types/Rules",
  UpcRecalls: "UPC Recalls",
  AdGroups: "Ad Groups",
  AdBlocks: "Ad Blocks",
  Scanbacks: "Scanbacks",
  SecondaryPromotions: "Secondary Promotions",
  SpecialtyPromotions: "Specialty Promotions",
  ShipperMaint: "Shipper Maint",
  DisplayShippers: "Display Shippers",
  ShipperRetail: "Shipper Retail",
  MaintainPromotionPrices: "Maintain Promotion Prices",
  AddFuturePromotions: "Add Future Promotions",
  PromoPricingByBanner: "Promo Pricing by Banner",
  RegularPricing: "Regular Pricing by Banner",
  PromoPricing: "Promotional Pricing",
  AddPendingPricing: "Add Future Prices",
  EOffers: "eOffers (Digital Coupons)",
};

export const APIS = {
  UserClaims: `${ITEMS_BASE_API_URL}/api/utility/User/Claims`,
  UserProfile: `${ITEMS_BASE_API_URL}/api/utility/User/Profile`,
  // Legacy del_price_warnings — advisory only, fetched when a delete
  // confirmation opens rather than raised after the user has agreed.
  RegularDeleteAdvisories: (ids: number[]) =>
    `${ITEMS_BASE_API_URL}/api/item/RetailPrice/delete-advisories?ids=${ids.join(",")}`,
  UserPermissions: `${ITEMS_BASE_API_URL}/api/user/UserPermissions`,
  /**
   * QA Access (RP_USER_ROLES self-service).
   *
   * All three are ALREADY DECLARED on the production APIM, which is why the
   * screen uses them rather than a purpose-built controller: an undeclared
   * operation answers with no CORS headers, so the browser reports a bare
   * network error instead of a 404 and no status-code fallback fires.
   * `SecurityRoleCatalogue` is the same `distinct-roles` url with a richer
   * RESPONSE — response bodies are not validated by the gateway.
   */
  SecurityUserRole: `${ITEMS_BASE_API_URL}/api/admin/SecurityUserRole`,
  SecurityUserRoleByKey: (roleName: string, userName: string) =>
    `${ITEMS_BASE_API_URL}/api/admin/SecurityUserRole/` +
    `${encodeURIComponent(roleName)}/${encodeURIComponent(userName)}`,
  SecurityRoleCatalogue: `${ITEMS_BASE_API_URL}/api/admin/SecurityUserRole/distinct-roles`,
  RPProgramData: `${ITEMS_BASE_API_URL}/api/programdata/RPProgramData?filter=BATCH-UPLOAD-TYP-DDL&field=variablename&page=1&limit=25`,
  // One RP_PROGRAM_DATA row by name, through the same generic endpoint the
  // hardcoded lookups above already use — so a new configured threshold needs
  // no new APIM operation.
  ProgramDataByName: (variableName: string) =>
    `${ITEMS_BASE_API_URL}/api/programdata/RPProgramData?filter=${encodeURIComponent(variableName)}&field=variablename&page=1&limit=1`,
  DownloadTemplate: (templateName: string) =>
    `${ITEMS_BASE_API_URL}/api/item/batch/BatchTemplate/download?templateName=${templateName}`,
  BatchStatusWk: `${ITEMS_BASE_API_URL}/api/bm/BatchStatusWk`,
  BatchStatusWkExtended: `${ITEMS_BASE_API_URL}/api/bm/BatchStatusWk/extended`,
  ItemBatchErrorsWk: `${ITEMS_BASE_API_URL}/api/bm/ItemBatchErrorsWk`,
  BatchStatusWKCancel: (batchNumber: string) =>
    `${ITEMS_BASE_API_URL}/api/bm/BatchStatusWk/cancel/${batchNumber}`,
  uploadFile: (type: string) =>
    `${ITEMS_BASE_API_URL}/api/item/batch/BatchUpload/upload?type=${type}&userId=9999999`,
  StoresList: `${STORES_BASE_API_URL}/api/store/Store`,
  PromoAddWk: (batchNumber: string) =>
    `${ITEMS_BASE_API_URL}/api/bm/PromoAddWk?filter=${batchNumber}&field=bchNo`,
  ItemDeleteWk: (batchNumber: string) =>
    `${ITEMS_BASE_API_URL}/api/bm/ItemDeleteWk?filter=${batchNumber}&field=bchNo`,
  ItemAddWk: (batchNumber: string) =>
    `${ITEMS_BASE_API_URL}/api/bm/ItemAddWk?filter=${batchNumber}&field=bchNo`,
  PriceChangeWk: (batchNumber: string) =>
    `${ITEMS_BASE_API_URL}/api/bm/PriceChangeWk?filter=${batchNumber}&field=bchNo`,
  ScanBackWk: (batchNumber: string) =>
    `${ITEMS_BASE_API_URL}/api/bm/ScanBackWk?filter=${batchNumber}&field=bchNo`,
  StoreDetails: (id: string) => `${STORES_BASE_API_URL}/api/store/Store/${id}`,
  ValidateStoreUpdate: `${STORES_BASE_API_URL}/api/StoreValidation`,
  DealCost: `${COST_BASE_API_URL}/api/cost/ItemCostZoneAllow`,
  UpdateStore: (id: string) => `${STORES_BASE_API_URL}/api/store/Store/${id}`,
  CreateStore: () => `${STORES_BASE_API_URL}/api/store/Store`,
  NextBaseStoreNumber: `${STORES_BASE_API_URL}/api/store/Store/next-base-store-number`,
  Division: `${STORES_BASE_API_URL}/api/store/Division?page=1&limit=1000`,
  AdZone: `${STORES_BASE_API_URL}/api/store/ADZone?page=1&limit=1000`,
  Banner: `${STORES_BASE_API_URL}/api/store/Banner?page=1&limit=1000`,
  BannerByCode: (bannerCode: string) =>
    `${STORES_BASE_API_URL}/api/store/Banner/${encodeURIComponent(bannerCode)}`,
  AvailableBannersForSupplier: (supplierNumber: number | string) =>
    `${ITEMS_BASE_API_URL}/api/store/Banner/available?supplierNumber=${encodeURIComponent(
      String(supplierNumber)
    )}`,
  DivisionBanner: `${STORES_BASE_API_URL}/api/store/DivisionBannerCode?page=1&limit=1000`,
  StoreStatus: `${STORES_BASE_API_URL}/api/store/StoreStatus?page=1&limit=1000`,
  StoreBanner: `${STORES_BASE_API_URL}/api/store/StoreBanner?page=1&limit=1000`,
  StoreType: `${STORES_BASE_API_URL}/api/store/StoreType?page=1&limit=1000`,
  State: `${STORES_BASE_API_URL}/api/store/State?page=1&limit=1000`,
  County: `${STORES_BASE_API_URL}/api/store/County?page=1&limit=1000`,
  Counties: (stateCode: string) =>
    `${STORES_BASE_API_URL}/api/store/County?page=1&limit=1000&field=stateCode&filter=${encodeURIComponent(stateCode)}`,
  StoreAuthorizationGroup: `${STORES_BASE_API_URL}/api/store/StoreAuthorizationGroup?sortProperties=storeAuthorizationGroupNumber&page=1&exactMatch=false`,
  Equipment: `${STORES_BASE_API_URL}/api/store/Equipment`,
  SoftwareLevel: `${STORES_BASE_API_URL}/api/store/SoftwareLevel?page=1&limit=1000`,
  BonusPerksType: `${ITEMS_BASE_API_URL}/api/programdata/RPProgramData?filter=BONUS-PERKS-VERSION&field=VariableName`,
  Suppliers: `${SUPPLIERS_BASE_API_URL}/api/Supplier`,
  UnstampedSupplier: `${SUPPLIERS_BASE_API_URL}/api/Supplier?field=supplierTypeCode&filter=1&limit=1000`,
  SupplierColorBar: `${SUPPLIERS_BASE_API_URL}/api/TagColorBarImage?page=1&limit=1000`,
  SupplierPriceBook: `${SUPPLIERS_BASE_API_URL}/api/PriceBookCode?page=1&limit=1000`,
  SupplierType: `${SUPPLIERS_BASE_API_URL}/api/SupplierType?page=1&limit=1000`,
  apVendorNumber: `${SUPPLIERS_BASE_API_URL}/api/ApSupplierShadow?page=1&limit=30000&exactMatch=false`,
  apVendorNumberSearch: (searchTerm: string) => {
    // If search starts with a number, filter by ID; otherwise filter by name
    const filterField = /^\d/.test(searchTerm)
      ? "apSupplierId"
      : "apSupplierName";
    return `${SUPPLIERS_BASE_API_URL}/api/ApSupplierShadow?page=1&limit=50&exactMatch=false&field=${filterField}&filter=${encodeURIComponent(searchTerm)}`;
  },
  SupplierSysCode: `${SUPPLIERS_BASE_API_URL}/api/GESystem?page=1&limit=1000`,
  NextSupplierNumber: `${SUPPLIERS_BASE_API_URL}/api/Supplier/available-id`,
  NextWholesaleSupplierNumber: `${SUPPLIERS_BASE_API_URL}/api/Supplier/available-id?isWholeSale=true`,
  SuppliersUnstamped: `${SUPPLIERS_BASE_API_URL}/api/Supplier?page=1&limit=1000&field=supplierTypeCode&filter=1`,
  RetailItemAuthorization: `${ITEMS_BASE_API_URL}/api/item/RetailItemAuthorization/search`,
  RetailItemAuthorizationWarmup: `${ITEMS_BASE_API_URL}/api/item/RetailItemAuthorization/search/warmup`,
  StoreItemAttributes: `${ITEMS_BASE_API_URL}/api/store-item-attributes/upsert`,
  DsdSuppliers: `${ITEMS_BASE_API_URL}/api/item/RetailItemAuthorization/dsd-suppliers`,
  AllSuppliers: `${ITEMS_BASE_API_URL}/api/item/RetailItemAuthorization/suppliers`,
  SupplierItems: (supplierNumber: number) =>
    `${ITEMS_BASE_API_URL}/api/item/RetailItemAuthorization/supplier-items/${supplierNumber}`,
  SuppliersForItem: (ritemNo: number) =>
    `${ITEMS_BASE_API_URL}/api/item/RetailItemAuthorization/suppliers-for-item/${ritemNo}`,
  AuthStores: (supplierNumber: number, ritemNumbers: number[]) =>
    `${ITEMS_BASE_API_URL}/api/item/RetailItemAuthorization/auth-stores?supplierNumber=${supplierNumber}${ritemNumbers.map((r) => `&ritemNumbers=${r}`).join("")}`,
  SearchBulk: `${ITEMS_BASE_API_URL}/api/item/RetailItemAuthorization/search-bulk`,
  SearchAdvanced: `${ITEMS_BASE_API_URL}/api/item/RetailItemAuthorization/search-advanced`,
  ValidateValues: `${ITEMS_BASE_API_URL}/api/item/RetailItemAuthorization/validate`,
  DsdStores: `${ITEMS_BASE_API_URL}/api/item/RetailItemAuthorization/dsd-stores`,
  AllStores: `${ITEMS_BASE_API_URL}/api/item/RetailItemAuthorization/stores`,
  // bannerCode changes what sysCode means for type=whitem: with it, sysCode is
  // matched against the per-item CONTROLLING price group's system code
  // (ALTERNATE_PRICE_GROUP / RETAIL_ITEM); without it, against the supplier's
  // own SUPPLIER.SYS_CD. Pick whichever question the caller is actually
  // asking. Family Maint deliberately omits it: legacy add_item matches a
  // WHITEM # on SUPPLIER.SYS_CD, and the code it passes is the family's own
  // (FAMILY.PRC_GRP_CD -> PRICE_GROUP.SYS_CD), so the supplier selected here
  // is the one that supplies the whitem shown in the grid (FMIT_SPIT_VW).
  // taggableOnly applies legacy's active_cur rule (the item needs at least one
  // Active or Discontinued supplier row). The tag-request screens pass it so
  // the typeahead cannot offer an item the preview will refuse — MERCHIT-3389.
  SearchItems: (
    type: string,
    term: string,
    sysCode?: string,
    supplierNumber?: number,
    bannerCode?: string,
    taggableOnly?: boolean,
    exactMatch?: boolean
  ) =>
    `${ITEMS_BASE_API_URL}/api/item/RetailItemAuthorization/search-items?type=${type}&term=${encodeURIComponent(term)}${
      sysCode ? `&sysCode=${encodeURIComponent(sysCode)}` : ""
    }${supplierNumber != null ? `&supplierNumber=${supplierNumber}` : ""}${
      bannerCode ? `&bannerCode=${encodeURIComponent(bannerCode)}` : ""
    }${taggableOnly ? "&taggableOnly=true" : ""}${
      exactMatch ? "&exactMatch=true" : ""
    }`,
  GetAuthorizationDetails: (
    itemNumber: number,
    supplierNumber: number,
    storeNumber: number
  ) =>
    `${ITEMS_BASE_API_URL}/api/item/RetailItemAuthorization/${itemNumber}/${supplierNumber}/${storeNumber}/details`,
  GetRanksForItemStore: (itemNumber: number, storeNumber: number) =>
    `${ITEMS_BASE_API_URL}/api/item/RetailItemAuthorization/${itemNumber}/${storeNumber}/ranks`,
  ChangeAuthorization: `${ITEMS_BASE_API_URL}/api/item/RetailItemAuthorization/change-authorization`,
  /**
   * Rank changes — single row, OR a whole batch as `{ rows: [...] }`.
   *
   * The batch goes here rather than to `change-rank-batch` because that is a
   * NEW gateway operation: it is declared in the spec repo but has never been
   * applied to the production APIM, and an undeclared operation returns no CORS
   * headers, so the browser sees a bare Network Error rather than a 404. This
   * URL is already declared and live, APIM routes on method + URL template
   * alone, and the spec is imported with no `validate-content` policy — so the
   * extra property rides through untouched and the fix ships with no refresh.
   */
  ChangeRank: `${ITEMS_BASE_API_URL}/api/item/RetailItemAuthorization/change-rank`,
  /**
   * The natural home for the batch, kept for when the APIM refresh happens.
   * Deliberately NOT called today — see ChangeRank above.
   */
  ChangeRankBatch: `${ITEMS_BASE_API_URL}/api/item/RetailItemAuthorization/change-rank-batch`,
  UpdateTagCount: `${ITEMS_BASE_API_URL}/api/item/RetailItemAuthorization/update-tag-count`,
  SendDsdMaintenance: `${ITEMS_BASE_API_URL}/api/item/RetailItemAuthorization/send-dsd-maintenance`,
  SendPosMaintenance: `${ITEMS_BASE_API_URL}/api/item/RetailItemAuthorization/send-pos-maintenance`,
  SendDsdMaintenanceBatch: `${ITEMS_BASE_API_URL}/api/item/RetailItemAuthorization/send-dsd-maintenance-batch`,
  SendPosMaintenanceBatch: `${ITEMS_BASE_API_URL}/api/item/RetailItemAuthorization/send-pos-maintenance-batch`,
  AuthorizationFlags: `${ITEMS_BASE_API_URL}/api/item/RetailItemAuthorization/authorization-flags`,
  UpdateAuthorizationBatch: `${ITEMS_BASE_API_URL}/api/item/RetailItemAuthorization/update-batch`,

  ItemStateFlags: (type: string, itemNumber: string) =>
    `${ITEMS_BASE_API_URL}/api/item/ItemStateFlag/search?${type}=${itemNumber}`,
  SnapItemSubgroups: `${ITEMS_BASE_API_URL}/api/item/SubGroup`,
  Subgroups: (groupCode: string) =>
    `${ITEMS_BASE_API_URL}/api/item/SubGroup?filter=${groupCode}&field=groupCode&page=1&limit=4000&exactMatch=true`,
  StateSubgroups: (groupCode: string, subgroupCode: string) =>
    `${ITEMS_BASE_API_URL}/api/item/StateSubGroup?filter=${subgroupCode}&field=subGroupCode&filter=${groupCode}&field=groupCode&page=1&limit=1000`,
  PosTenderRestrictions: `${ITEMS_BASE_API_URL}/api/programdata/RPProgramData?filter=POS-TNDR-RSTR-GRP-CD&field=VARIABLENAME&page=1&limit=25`,
  WineCaseDiscountCode: `${ITEMS_BASE_API_URL}/api/WineCaseDiscountCode?page=1&limit=1000`,
  POSRestrictedSaleType: `${ITEMS_BASE_API_URL}/api/POSRestrictedSaleType?page=1&limit=1000`,
  Groups: () =>
    `${ITEMS_BASE_API_URL}/api/Group?page=1&limit=1000&sortProperties=groupCode&sortDirections=asc`,
  Categories: () =>
    `${ITEMS_BASE_API_URL}/api/Category?page=1&limit=1000&sortProperties=categoryCode&sortDirections=asc`,
  /**
   * Legacy's RETAIL_PRICE half of the WIC_HI_PRC trigger — a warning, never a
   * refusal. Resolves the zones serving the item's price group in that state
   * and reports any current price above the proposed maximum.
   */
  WicHighPriceCheck: (ritemNo: number, stCd: string, wicHiPrc: number) =>
    `${ITEMS_BASE_API_URL}/api/item/ItemStateFlag/wic-price-check` +
    `?ritemNo=${ritemNo}&stCd=${encodeURIComponent(stCd)}&wicHiPrc=${wicHiPrc}`,
  UpdateItemStateFlag: (ritemNo: number, stCd: string) =>
    `${ITEMS_BASE_API_URL}/api/item/ItemStateFlag/${ritemNo}/${stCd}`,
  UpdateStateSubgroup: (
    groupCode: number,
    subGroupCode: number,
    stateCode: string
  ) =>
    `${ITEMS_BASE_API_URL}/api/item/StateSubGroup/${groupCode}/${subGroupCode}/${stateCode}`,
  CopyStore: ({
    storeNumber,
    newStoreNumber,
    newBannerCode,
    newSagNo,
    newStoreName,
  }: CopyFormInputs) => {
    return `${STORES_BASE_API_URL}/api/store/Store/copy-store?newStoreNumber=${newStoreNumber}&newStoreName=${newStoreName}&storeNumber=${storeNumber}&newBannerCode=${newBannerCode}&newSagNo=${newSagNo}`;
  },
  CopyStoreStatus: (jobId: string) =>
    `${STORES_BASE_API_URL}/api/store/Store/copy-store/status/${jobId}`,
  CopyDsdData: `${STORES_BASE_API_URL}/api/store/Store/copy-dsd-data`,
  CopyDsdDataStatus: (jobId: string) =>
    `${STORES_BASE_API_URL}/api/store/Store/copy-dsd-data/status/${jobId}`,
  CostZone: (upc: number | null, supplierNumber: number | null) =>
    `${COST_BASE_API_URL}/api/cost/ItemCostZoneCost?filter=${supplierNumber}&filter=${upc}&field=suppliernumber&field=upc&page=1&limit=1000&exactMatch=true`,

  ItemCostHistory: `${COST_BASE_API_URL}/api/ItemCostHistory`,
  CostZoneDescription: (
    costZoneNumber: number | null,
    supplierSelected: number | null
  ) =>
    `${COST_BASE_API_URL}/api/store/CostZone/${costZoneNumber}/${supplierSelected}`,
  ItemCostZoneCostById: (itemCostId: number) =>
    `${COST_BASE_API_URL}/api/cost/ItemCostZoneCost/${itemCostId}`,
  ItemCostZoneCostPost: `${COST_BASE_API_URL}/api/cost/ItemCostZoneCost`,
  ItemCostZoneCostBulkPost: `${COST_BASE_API_URL}/api/cost/ItemCostZoneCost/bulk`,
  CostZonesBySupplier: (supplierNumber: number | null) =>
    `${COST_BASE_API_URL}/api/store/CostZone?filter=${supplierNumber}&field=supplierNumber&exactMatch=true`,
  CostLink: `${COST_BASE_API_URL}/api/CostLink`,
  CostLinkFamily: `${COST_BASE_API_URL}/api/CostLinkFamily`,
  SupplierItem: `${ITEMS_BASE_API_URL}/api/item/SupplierItem`,
  SupplierItemByUpc: (upc: string | number) =>
    `${ITEMS_BASE_API_URL}/api/item/SupplierItem?filter=${encodeURIComponent(
      String(upc)
    )}&field=upc&page=1&limit=1&exactMatch=true`,
  SupplierItemByUpcAndSupplier: (
    upc: string | number,
    supplierNumber: number
  ) =>
    `${ITEMS_BASE_API_URL}/api/item/SupplierItem?field=upc&field=supplierNumber&filter=${encodeURIComponent(
      String(upc)
    )}&filter=${supplierNumber}&page=1&limit=10&exactMatch=true`,
  RetailItemById: (ritemNumber: string | number) =>
    `${ITEMS_BASE_API_URL}/api/item/RetailItem/${ritemNumber}`,
  CorpBrands: `${ITEMS_BASE_API_URL}/api/item/lookup/corpbrands`,
  StoresByCostZone: (
    supplierNumber: number | null,
    costZoneNumber: number | null
  ) =>
    `${ITEMS_BASE_API_URL}/api/store/StoreSupplier?filter=${supplierNumber}&filter=${costZoneNumber}&field=suppliernumber&field=costzonenumber&page=1&limit=1000&exactMatch=true`,
  StoreSupplierByStore: (storeNumber: number) =>
    `${ITEMS_BASE_API_URL}/api/store/StoreSupplier/bystore/${storeNumber}`,
  UnauthorizedWarehouses: (storeNumber: number) =>
    `${ITEMS_BASE_API_URL}/api/store/StoreSupplier/unauthorized-warehouses/${storeNumber}`,
  OraclePromotionList: `${ITEMS_BASE_API_URL}/api/Promotion/oracle`,
  OraclePromotionCreate: `${ITEMS_BASE_API_URL}/api/Promotion/oracle`,
  OraclePromotionUpdate: (promId: number) =>
    `${ITEMS_BASE_API_URL}/api/Promotion/oracle/${promId}`,
  OraclePromotionDetails: (promId: number) =>
    `${ITEMS_BASE_API_URL}/api/Promotion/oracle/${promId}`,
  PromotionZoneTree: (prcGrpCd: number, bnrCd: string) =>
    `${ITEMS_BASE_API_URL}/api/Promotion/zones/tree?prcGrpCd=${prcGrpCd}&bnrCd=${encodeURIComponent(bnrCd)}`,
  PromotionItemOverlapCheck: `${ITEMS_BASE_API_URL}/api/Promotion/item-overlap-check`,
  PromotionLookupBanners: `${ITEMS_BASE_API_URL}/api/PromotionLookup/banners`,
  // LOVs for the Promotion Types/Rules grid dropdowns.
  PromotionLookupActions: `${ITEMS_BASE_API_URL}/api/PromotionLookup/promotion-actions`,
  PromotionLookupBenchmarks: `${ITEMS_BASE_API_URL}/api/PromotionLookup/promotion-benchmarks`,
  PromotionLookupPriceTypes: `${ITEMS_BASE_API_URL}/api/PromotionLookup/price-types`,
  PromotionLookupPriceGroups: `${ITEMS_BASE_API_URL}/api/PromotionLookup/price-groups`,
  PromotionLookupMarketingMessages: `${ITEMS_BASE_API_URL}/api/PromotionLookup/marketing-messages`,
  PromotionProgramData: (variableName: string) =>
    `${ITEMS_BASE_API_URL}/api/PromotionLookup/program-data/${encodeURIComponent(variableName)}`,
  PromotionList: `${ITEMS_BASE_API_URL}/api/Promotion`,
  PromotionDetails: (guid: string) =>
    `${ITEMS_BASE_API_URL}/api/Promotion/${guid}`,
  PromotionCreate: `${ITEMS_BASE_API_URL}/api/Promotion`,
  PromotionUpdate: (guid: string) =>
    `${ITEMS_BASE_API_URL}/api/Promotion/${guid}`,
  PromotionDelete: (guid: string) =>
    `${ITEMS_BASE_API_URL}/api/Promotion/${guid}`,
  PromotionApprovalSubmit: (promotionGuid: string) =>
    `${ITEMS_BASE_API_URL}/api/PromotionApproval/submit?promotionGuid=${promotionGuid}`,
  CopyStoreZones: `${STORES_BASE_API_URL}/api/store/Zone/copy-store-zones`,
  ZonesByStore: (storeNumber: number) =>
    `${STORES_BASE_API_URL}/api/store/SystemZoneSupplierCust/by-store/${storeNumber}`,
  TagHandlingInstruction: `${ITEMS_BASE_API_URL}/api/item/TagHandlingInstruction`,
  TagHandlingInstructionById: (id: number) =>
    `${ITEMS_BASE_API_URL}/api/item/TagHandlingInstruction/${id}`,
  TagHandlingInstructionDeliveryMethods: `${ITEMS_BASE_API_URL}/api/item/TagHandlingInstruction/delivery-methods`,
  // eOffers (digital coupons) - EOfferController on the Items API.
  EOfferSummary: `${ITEMS_BASE_API_URL}/api/item/EOffer/summary`,
  EOfferSearch: `${ITEMS_BASE_API_URL}/api/item/EOffer/search`,
  EOfferById: (eofferId: string) =>
    `${ITEMS_BASE_API_URL}/api/item/EOffer/${encodeURIComponent(eofferId)}`,
  EOfferHistory: `${ITEMS_BASE_API_URL}/api/item/EOffer/history`,
  EOfferExclusions: `${ITEMS_BASE_API_URL}/api/item/EOffer/exclusions`,
  EOfferExclusionsValidate: `${ITEMS_BASE_API_URL}/api/item/EOffer/exclusions/validate`,
  EOfferExclusionByCode: (code: string) =>
    `${ITEMS_BASE_API_URL}/api/item/EOffer/exclusions/${encodeURIComponent(code)}`,
  EOfferExclusionJournal: (code: string) =>
    `${ITEMS_BASE_API_URL}/api/item/EOffer/exclusions/${encodeURIComponent(code)}/journal`,
  PendingTagSubmitReplacementStore: `${ITEMS_BASE_API_URL}/api/item/PendingTag/submit-replacement-store`,
  PendingTagPreviewReplacementStore: `${ITEMS_BASE_API_URL}/api/item/PendingTag/preview-replacement-store`,
  PendingTagSubmitFullScan: `${ITEMS_BASE_API_URL}/api/item/PendingTag/submit-full-scan`,
  GeSystemList: `${SUPPLIERS_BASE_API_URL}/api/GESystem?page=1&limit=100`,
  Department: `${ITEMS_BASE_API_URL}/api/item/Department`,
  SubDepartment: (departmentCode?: number) =>
    departmentCode != null
      ? `${ITEMS_BASE_API_URL}/api/item/SubDepartment?departmentCode=${departmentCode}`
      : `${ITEMS_BASE_API_URL}/api/item/SubDepartment`,
  FuelperkEntryType: `${ITEMS_BASE_API_URL}/api/item/FuelperkEntryType`,
  StoreDeptXrefCopyFrom: (toStoreNumber: number, fromStoreNumber: number) =>
    `${STORES_BASE_API_URL}/api/store/StoreDeptXref/by-store/${toStoreNumber}/copy-from/${fromStoreNumber}`,
  // Async variant. The copy re-authorizes every item in any category whose
  // sub-department changes — measured at 226s for a single 79k-item category —
  // so it cannot complete inside a request. POST returns 202 + a job id; poll
  // the status URL below.
  StoreDeptXrefCopyFromAsync: (
    toStoreNumber: number,
    fromStoreNumber: number
  ) =>
    `${STORES_BASE_API_URL}/api/store/StoreDeptXref/by-store/${toStoreNumber}/copy-from/${fromStoreNumber}/async`,
  StoreDeptXrefCopyFromStatus: (
    toStoreNumber: number,
    fromStoreNumber: number,
    jobId: string
  ) =>
    `${STORES_BASE_API_URL}/api/store/StoreDeptXref/by-store/${toStoreNumber}/copy-from/${fromStoreNumber}/status/${jobId}`,
  JournalTables: `${ITEMS_BASE_API_URL}/api/item/Journal/tables`,
  JournalColumns: (tableName: string) =>
    `${ITEMS_BASE_API_URL}/api/item/Journal/${encodeURIComponent(tableName)}/columns`,
  JournalQuery: (tableName: string, params: string) =>
    `${ITEMS_BASE_API_URL}/api/item/Journal/${encodeURIComponent(tableName)}?${params}`,
  ScaleDepartment: `${ITEMS_BASE_API_URL}/api/item/ScaleDepartment`,
  ScaleDepartmentById: (code: string) =>
    `${ITEMS_BASE_API_URL}/api/item/ScaleDepartment/${encodeURIComponent(code)}`,
  ScaleLabelFormats: `${ITEMS_BASE_API_URL}/api/item/lookup/scalelabelformats`,
  ScaleTare: `${ITEMS_BASE_API_URL}/api/item/ScaleTare`,
  ScaleTareById: (code: number) =>
    `${ITEMS_BASE_API_URL}/api/item/ScaleTare/${code}`,
  CaseInterpackItem: `${ITEMS_BASE_API_URL}/api/item/CaseInterpackItem`,
  CaseInterpackItemById: (nei: number, reg: number) =>
    `${ITEMS_BASE_API_URL}/api/item/CaseInterpackItem/${nei}/${reg}`,
  ScaleTareImpact: (code: number) =>
    `${ITEMS_BASE_API_URL}/api/item/ScaleTare/impact/${code}`,
  ScaleTareItems: (code: number) =>
    `${ITEMS_BASE_API_URL}/api/item/ScaleTare/items/${code}`,
  PosFileCompare: `${STORES_BASE_API_URL}/api/store/PosFileCompare`,
  PosFileCompareLimits: `${STORES_BASE_API_URL}/api/store/PosFileCompare/limits`,
  UpcRecall: `${ITEMS_BASE_API_URL}/api/item/UpcRecall`,
  UpcRecallComment: (id: number) =>
    `${ITEMS_BASE_API_URL}/api/item/UpcRecall/${id}/comment`,
  StoreDeptXrefByStore: (storeNumber: number) =>
    `${STORES_BASE_API_URL}/api/store/StoreDeptXref/by-store/${storeNumber}`,
  RetailItem: (ritemNo: number) =>
    `${ITEMS_BASE_API_URL}/api/item/RetailItem/${ritemNo}`,
  RetailItemList: `${ITEMS_BASE_API_URL}/api/item/RetailItem`,
  MtcDetail: (ritemNo: number) =>
    `${ITEMS_BASE_API_URL}/api/item/RetailItem/${ritemNo}/mtc`,
  CouponCreate: `${ITEMS_BASE_API_URL}/api/item/RetailItem/coupon`,
  Family: `${ITEMS_BASE_API_URL}/api/item/Family`,
  FamilyById: (id: number) => `${ITEMS_BASE_API_URL}/api/item/Family/${id}`,
  FamilyItemsByFamily: (familyCode: number) =>
    `${ITEMS_BASE_API_URL}/api/item/FamilyItem/by-family/${familyCode}`,
  FamilyItemsByItem: (ritemNumber: number) =>
    `${ITEMS_BASE_API_URL}/api/item/FamilyItem/by-item/${ritemNumber}`,
  FamilyItem: `${ITEMS_BASE_API_URL}/api/item/FamilyItem`,
  FamilyItemBulk: `${ITEMS_BASE_API_URL}/api/item/FamilyItem/bulk`,
  FamilyItemDelete: (familyCode: number, ritemNumber: number) =>
    `${ITEMS_BASE_API_URL}/api/item/FamilyItem/${familyCode}/${ritemNumber}`,
  PriceDetailSearch: `${ITEMS_BASE_API_URL}/api/item/PriceDetail/search`,
  ScanbackBusinessUnits: `${ITEMS_BASE_API_URL}/api/item/lookup/scanback-business-units`,
  ScanbackEntryTypes: `${ITEMS_BASE_API_URL}/api/item/lookup/scanback-entry-types`,
  MiscTranItemTypes: `${ITEMS_BASE_API_URL}/api/item/lookup/misc-tran-item-types`,
  GiftCardRetailers: `${ITEMS_BASE_API_URL}/api/item/lookup/gift-card-retailers`,
  UpcMfrFamily: (upc: number) =>
    `${ITEMS_BASE_API_URL}/api/item/lookup/upc-mfr-family/${upc}`,
  // Promo-adjacent endpoints (Ad Groups / Ad Blocks / Scanbacks /
  // Secondary + Specialty Promotions / AccountPromotion / PriceType).
  // Originally lived on GE.API.RetailPricing.Promotions; folded into the
  // Items API in BE PR #280 because the Promotions .NET host on
  // iservicestest was 500'ing on every POST. Same controller code, same
  // route paths — only the gateway host changed.
  AdGroup: `${ITEMS_BASE_API_URL}/api/promotion/AdGroup`,
  AdGroupById: (id: number) =>
    `${ITEMS_BASE_API_URL}/api/promotion/AdGroup/${id}`,
  AdGroupItems: (id: number) =>
    `${ITEMS_BASE_API_URL}/api/promotion/AdGroup/${id}/items`,
  AdGroupItemById: (id: number, itemId: number) =>
    `${ITEMS_BASE_API_URL}/api/promotion/AdGroup/${id}/items/${itemId}`,
  AdGroupFamilyMemberships: (id: number) =>
    `${ITEMS_BASE_API_URL}/api/promotion/AdGroup/${id}/family-memberships`,
  AdBlock: `${ITEMS_BASE_API_URL}/api/promotion/AdBlock`,
  AdBlockById: (id: number) =>
    `${ITEMS_BASE_API_URL}/api/promotion/AdBlock/${id}`,
  AdBlockLookup: (type: string) =>
    `${ITEMS_BASE_API_URL}/api/promotion/AdBlock/lookups/${type}`,
  AdBlockItems: (id: number) =>
    `${ITEMS_BASE_API_URL}/api/promotion/AdBlock/${id}/items`,
  AdBlockItemById: (id: number, itemId: number) =>
    `${ITEMS_BASE_API_URL}/api/promotion/AdBlock/${id}/items/${itemId}`,
  AdBlockZones: (id: number) =>
    `${ITEMS_BASE_API_URL}/api/promotion/AdBlock/${id}/zones`,
  AdBlockZoneById: (id: number, eopzId: number) =>
    `${ITEMS_BASE_API_URL}/api/promotion/AdBlock/${id}/zones/${eopzId}`,
  AdBlockExclusions: (id: number) =>
    `${ITEMS_BASE_API_URL}/api/promotion/AdBlock/${id}/exclusions`,
  AdBlockExclusionByRitem: (id: number, ritemNo: number) =>
    `${ITEMS_BASE_API_URL}/api/promotion/AdBlock/${id}/exclusions/${ritemNo}`,
  AdBlockOtherProms: (id: number) =>
    `${ITEMS_BASE_API_URL}/api/promotion/AdBlock/${id}/other-promos`,
  AdBlockOtherPromById: (id: number, typId: number, promId: number) =>
    `${ITEMS_BASE_API_URL}/api/promotion/AdBlock/${id}/other-promos/${typId}/${promId}`,
  AdBlockSendToCircular: (id: number) =>
    `${ITEMS_BASE_API_URL}/api/promotion/AdBlock/${id}/send-to-circular`,
  AdBlockOtherPromoLookup: (typeId: number, promoId: number) =>
    `${ITEMS_BASE_API_URL}/api/promotion/AdBlock/other-promo-lookup/${typeId}/${promoId}`,
  AdBlockByCatMgrAll: `${ITEMS_BASE_API_URL}/api/promotion/AdBlock/by-category-manager`,
  AdBlockByCatMgr: (catMgrCd: number) =>
    `${ITEMS_BASE_API_URL}/api/promotion/AdBlock/by-category-manager/${catMgrCd}`,
  AdBlockByItemAll: `${ITEMS_BASE_API_URL}/api/promotion/AdBlock/by-item`,
  AdBlockByItem: (ritemNo: number) =>
    `${ITEMS_BASE_API_URL}/api/promotion/AdBlock/by-item/${ritemNo}`,
  // BE PR #280 fold-in copied ScanbackController verbatim from the legacy
  // Promotions API, which used [Route("api/[controller]")] — not the
  // `api/promotion/` prefix the other folded controllers (AdGroup / AdBlock
  // / Secondary / Specialty) use. The apiops items spec also serves it at
  // /api/Scanback. Drop the `promotion/` segment so the FE actually reaches
  // the route instead of 404'ing silently.
  Scanback: `${ITEMS_BASE_API_URL}/api/Scanback`,
  ScanbackBillingDefaults: `${ITEMS_BASE_API_URL}/api/dealmanagement/scanback-billing-defaults`,
  ScanbackStores: `${ITEMS_BASE_API_URL}/api/Scanback/stores`,
  ScanbackResolveItem: `${ITEMS_BASE_API_URL}/api/Scanback/resolve-item`,
  ScanbackArLookup: `${ITEMS_BASE_API_URL}/api/Scanback/ar-lookup`,
  ScanbackPerspectiveLookup: `${ITEMS_BASE_API_URL}/api/Scanback/perspective-lookup`,
  ScanbackSuppliers: `${ITEMS_BASE_API_URL}/api/Scanback/suppliers`,
  // RP_PROGRAM_DATA 'SCNBCK-MAINT-TYPE' — the scanback types this
  // screen maintains, which legacy pinned in its block WHERE clause.
  ScanbackMaintTypes: `${ITEMS_BASE_API_URL}/api/Scanback/maint-types`,
  // Legacy add_all_zone (PRC_SCANBACK_MAINT.txt:34993) — the zones one
  // decision fans out across, and the action that writes them.
  ScanbackZonesFor: (prcGrpCd: number, bnrCd: string, splrNo?: number | null) =>
    `${ITEMS_BASE_API_URL}/api/Scanback/zones-for?prcGrpCd=${prcGrpCd}&bnrCd=${encodeURIComponent(bnrCd)}` +
    (splrNo != null ? `&splrNo=${splrNo}` : ""),
  ScanbackCreateForAllZones: `${ITEMS_BASE_API_URL}/api/Scanback/create-for-all-zones`,
  // Legacy change_values (PRC_SCANBACK_MAINT.txt:35354) — the defaults
  // console applied across every listed row.
  ScanbackApplyToDisplayed: `${ITEMS_BASE_API_URL}/api/Scanback/apply-to-displayed`,
  // Legacy Exclude Store/Override (PRC_SCANBACK_MAINT.txt:25576) — create
  // for every store in the named zones except the listed exclusions.
  ScanbackCreateExcluding: `${ITEMS_BASE_API_URL}/api/Scanback/create-excluding`,
  // Legacy Items in Family window (PRC_SCANBACK_MAINT.txt:25742).
  // Legacy Model after Promotion (PRC_SCANBACK_MAINT.txt:34914).
  ScanbackPromotionShape: (
    ritemNo: number,
    bnrCd: string,
    promCd: number,
    prcStrtDte: string
  ) =>
    `${ITEMS_BASE_API_URL}/api/Scanback/promotion-shape?ritemNo=${ritemNo}` +
    `&bnrCd=${encodeURIComponent(bnrCd)}&promCd=${promCd}&prcStrtDte=${prcStrtDte}`,
  ScanbackCreateModeledOnPromotion: (promCd: number, prcStrtDte: string) =>
    `${ITEMS_BASE_API_URL}/api/Scanback/create-modeled-on-promotion?promCd=${promCd}&prcStrtDte=${prcStrtDte}`,
  ScanbackFamilySiblings: (
    ritemNo: number,
    bnrCd: string,
    fmlyCd?: number | null
  ) =>
    `${ITEMS_BASE_API_URL}/api/Scanback/family-siblings?ritemNo=${ritemNo}&bnrCd=${encodeURIComponent(bnrCd)}` +
    (fmlyCd != null ? `&fmlyCd=${fmlyCd}` : ""),
  // Legacy add_group (PRC_SCANBACK_MAINT.txt:35070) — one scanback per
  // eligible store in a promotion group.
  ScanbackPromoGroupStores: (
    promoGrpId: number,
    prcGrpCd: number,
    bnrCd: string,
    splrNo?: number | null
  ) =>
    `${ITEMS_BASE_API_URL}/api/Scanback/promo-group-stores?promoGrpId=${promoGrpId}&prcGrpCd=${prcGrpCd}&bnrCd=${encodeURIComponent(bnrCd)}` +
    (splrNo != null ? `&splrNo=${splrNo}` : ""),
  ScanbackCreateForPromoGroup: (promoGrpId: number) =>
    `${ITEMS_BASE_API_URL}/api/Scanback/create-for-promo-group?promoGrpId=${promoGrpId}`,
  ScanbackById: (id: number) => `${ITEMS_BASE_API_URL}/api/Scanback/${id}`,
  SecondaryPromotion: `${ITEMS_BASE_API_URL}/api/promotion/SecondaryPromotion`,
  SecondaryPromotionById: (id: number) =>
    `${ITEMS_BASE_API_URL}/api/promotion/SecondaryPromotion/${id}`,
  SecondaryPromotionItems: (id: number) =>
    `${ITEMS_BASE_API_URL}/api/promotion/SecondaryPromotion/${id}/items`,
  SecondaryPromotionItemById: (id: number, spiId: number) =>
    `${ITEMS_BASE_API_URL}/api/promotion/SecondaryPromotion/${id}/items/${spiId}`,
  SecondaryPromotionStores: (id: number) =>
    `${ITEMS_BASE_API_URL}/api/promotion/SecondaryPromotion/${id}/stores`,
  SecondaryPromotionStoreById: (id: number, storeNumber: number) =>
    `${ITEMS_BASE_API_URL}/api/promotion/SecondaryPromotion/${id}/stores/${storeNumber}`,
  SpecialtyPromotion: `${ITEMS_BASE_API_URL}/api/promotion/SpecialtyPromotion`,
  SpecialtyPromotionTypes: `${ITEMS_BASE_API_URL}/api/promotion/SpecialtyPromotion/types`,
  SpecialtyPromotionById: (id: number) =>
    `${ITEMS_BASE_API_URL}/api/promotion/SpecialtyPromotion/${id}`,
  SpecialtyPromotionItems: (id: number) =>
    `${ITEMS_BASE_API_URL}/api/promotion/SpecialtyPromotion/${id}/items`,
  SpecialtyPromotionItemById: (id: number, sppiId: number) =>
    `${ITEMS_BASE_API_URL}/api/promotion/SpecialtyPromotion/${id}/items/${sppiId}`,
  SpecialtyPromotionStores: (id: number) =>
    `${ITEMS_BASE_API_URL}/api/promotion/SpecialtyPromotion/${id}/stores`,
  SpecialtyPromotionStoreById: (id: number, storeNumber: number) =>
    `${ITEMS_BASE_API_URL}/api/promotion/SpecialtyPromotion/${id}/stores/${storeNumber}`,
  // Shipper creation has its own endpoint because the two legacy screens
  // disagree about type 'S' on purpose: Item Maintenance refuses it
  // (ITM_ITEM_MAINT:8511), Shipper Maint requires it
  // (ITM_SHIPPER_MAINT:7459). Posting a shipper to the plain RetailItem
  // create hit the Item Maintenance rule and always 400'd.
  RetailItemShipper: `${ITEMS_BASE_API_URL}/api/item/RetailItem/shipper`,
  // The two Display Shippers lenses. They are separate endpoints on purpose:
  // legacy orders them differently (WHITEM,UPC for the finder against
  // SYSTEM,WHITEM,UPC for "contained in"), so one shared grid cannot serve
  // both.
  // Legacy PRC_DISP_SHPR_RETAIL. Store is part of the PATH, not a query
  // parameter, because it is mandatory - the form's g_query_store contract.
  ShipperRetail: (shipperNumber: number, storeNumber: number, effectiveDate?: Date | null) =>
    `${ITEMS_BASE_API_URL}/api/item/ShipperItem/retail/${shipperNumber}/${storeNumber}` +
    // The LOCAL calendar date the user picked. toISOString() is UTC, which
    // is the previous day for any clock ahead of UTC (MERCHIT-3481).
    (effectiveDate
      ? `?effectiveDate=${effectiveDate.getFullYear()}-${String(effectiveDate.getMonth() + 1).padStart(2, "0")}-${String(effectiveDate.getDate()).padStart(2, "0")}`
      : ""),
  ShippersContainingItem: (itemNumber: number) =>
    `${ITEMS_BASE_API_URL}/api/item/ShipperItem/containing/${itemNumber}`,
  ShipperSupplierRows: (params: {
    ritemNumber?: string;
    upc?: string;
    whitemNumber?: string;
    description?: string;
    sysCd?: string;
    supplierNumber?: string;
  }) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && String(v).trim() !== "") q.set(k, String(v).trim());
    });
    // A shipper finder wants every supplier row, not one per item.
    q.set("ritemType", "S");
    q.set("oneRowPerItem", "false");
    return `${ITEMS_BASE_API_URL}/api/item/SupplierItem/search?${q.toString()}`;
  },
  ShipperItem: `${ITEMS_BASE_API_URL}/api/item/ShipperItem`,
  ShipperItemById: (shipperNumber: number, itemNumber: number) =>
    `${ITEMS_BASE_API_URL}/api/item/ShipperItem/${shipperNumber}/${itemNumber}`,
  // Is this component an NEI, and what does it cost INSIDE THIS CARTON? The
  // shipper number is part of the question - the same component costs
  // differently in different cartons, which is what SHPR_NO on
  // ITEM_COST_ZONE_COST is for.
  // Every component's carton cost at once, for the components grid.
  ShipperItemNeiAll: (shipperNumber: number) =>
    `${ITEMS_BASE_API_URL}/api/item/ShipperItem/nei/${shipperNumber}`,
  ShipperItemNei: (shipperNumber: number, itemNumber: number) =>
    `${ITEMS_BASE_API_URL}/api/item/ShipperItem/nei/${shipperNumber}/${itemNumber}`,
  // `Add Whse Shipper` (BTN-09). The preview is separate from the create so the
  // dialog can show what it is about to build, and ask the already-exists
  // question, before anything is written — legacy does all of it in one click.
  ShipperWarehousePreview: (systemCode: string, whitemNumber: number) =>
    `${ITEMS_BASE_API_URL}/api/item/ShipperItem/warehouse-preview` +
    `?systemCode=${encodeURIComponent(systemCode)}&whitemNumber=${whitemNumber}`,
  ShipperWarehouseCreate: (
    systemCode: string,
    whitemNumber: number,
    confirmAssociated?: boolean,
  ) =>
    `${ITEMS_BASE_API_URL}/api/item/ShipperItem/warehouse` +
    `?systemCode=${encodeURIComponent(systemCode)}&whitemNumber=${whitemNumber}` +
    (confirmAssociated == null ? "" : `&confirmAssociated=${confirmAssociated}`),
  // The form's PRE-QUERY resolution: shipper-wins UPC (QRY-02), 6-digit
  // expansion (QRY-03), system-code back-fill (QRY-15), and the New Item
  // answer that sits between found and not-found (QRY-16).
  // Edit a shipper's header (the five fields ITM_SHIPPER_MAINT allows); the
  // plain RetailItem PUT refuses type 'S'.
  // Add a DSD supplier to a shipper (the shipper form's SUPPLIER_ITEM block):
  // GET previews, POST adds.
  // 2026-10-22 shipper batch: the supplier goes on every UPC, with its cost.
  ShipperDsdSupplierAdd: (shipper: number, supplierNumber: number, cost: number | null, deliveredCost: number | null) =>
    `${ITEMS_BASE_API_URL}/api/item/ShipperItem/${shipper}/dsd-supplier?supplierNumber=${supplierNumber}` +
    (cost != null ? `&cost=${cost}` : "") +
    (deliveredCost != null ? `&deliveredCost=${deliveredCost}` : ""),
  // The NEW_ITEM shipper rows Add Whse Shipper can consume (a pick list).
  ShipperNewItemPending: (systemCode?: string | null) =>
    `${ITEMS_BASE_API_URL}/api/item/ShipperItem/new-item-shippers` +
    (systemCode ? `?systemCode=${encodeURIComponent(systemCode)}` : ""),
  // DSD shipper in one transaction: header + supplier row(s) + cost rows.
  ShipperDsdCreate: `${ITEMS_BASE_API_URL}/api/item/ShipperItem/dsd`,
  ShipperSupplierDeleteRules: (shipper: number) =>
    `${ITEMS_BASE_API_URL}/api/item/ShipperItem/${shipper}/supplier-delete-rules`,
  // Delete the whole shipper, legacy's way: flag every supplier row. GET previews, POST does it.
  ShipperFlagDelete: (shipper: number) =>
    `${ITEMS_BASE_API_URL}/api/item/ShipperItem/${shipper}/flag-delete`,
  ShipperDsdSupplier: (shipper: number, supplierNumber: number, upc?: number | null) =>
    `${ITEMS_BASE_API_URL}/api/item/ShipperItem/${shipper}/dsd-supplier?supplierNumber=${supplierNumber}` +
    (upc != null ? `&upc=${upc}` : ""),
  ShipperHeader: (ritemNumber: number) =>
    `${ITEMS_BASE_API_URL}/api/item/RetailItem/${ritemNumber}/shipper-header`,
  ShipperResolve: (p: {
    systemCode?: string | null;
    whitemNumber?: number | null;
    upc?: number | null;
    ritemNumber?: number | null;
  }) => {
    const q = new URLSearchParams();
    if (p.systemCode) q.set("systemCode", p.systemCode);
    if (p.whitemNumber != null) q.set("whitemNumber", String(p.whitemNumber));
    if (p.upc != null) q.set("upc", String(p.upc));
    if (p.ritemNumber != null) q.set("ritemNumber", String(p.ritemNumber));
    return `${ITEMS_BASE_API_URL}/api/item/ShipperItem/resolve?${q.toString()}`;
  },
  // A component keyed three ways (FLD-COMPKEY-01), returning its size and
  // size UOM too (FLD-SIZE-01).
  ShipperResolveComponent: (p: {
    systemCode?: string | null;
    whitemNumber?: number | null;
    upc?: number | null;
    itemNumber?: number | null;
  }) => {
    const q = new URLSearchParams();
    if (p.systemCode) q.set("systemCode", p.systemCode);
    if (p.whitemNumber != null) q.set("whitemNumber", String(p.whitemNumber));
    if (p.upc != null) q.set("upc", String(p.upc));
    if (p.itemNumber != null) q.set("itemNumber", String(p.itemNumber));
    return `${ITEMS_BASE_API_URL}/api/item/ShipperItem/resolve-component?${q.toString()}`;
  },
  // The RETAIL_ITEM PRE-INSERT UPC chain - nine refusals, none of them
  // enforceable by a trigger (WP-13, BTN-14, BTN-16, PSU-03/05/06/07).
  ShipperCheckUpc: (p: {
    upc?: number | null;
    upcType?: string | null;
    systemCode?: string | null;
    whitemNumber?: number | null;
    allocatedPseudoUpc?: number | null;
    viaWarehouseButton?: boolean;
    isNewItemFlow?: boolean;
  }) => {
    const q = new URLSearchParams();
    if (p.upc != null) q.set("upc", String(p.upc));
    if (p.upcType) q.set("upcType", p.upcType);
    if (p.systemCode) q.set("systemCode", p.systemCode);
    if (p.whitemNumber != null) q.set("whitemNumber", String(p.whitemNumber));
    if (p.allocatedPseudoUpc != null)
      q.set("allocatedPseudoUpc", String(p.allocatedPseudoUpc));
    if (p.viaWarehouseButton) q.set("viaWarehouseButton", "true");
    if (p.isNewItemFlow) q.set("isNewItemFlow", "true");
    return `${ITEMS_BASE_API_URL}/api/item/ShipperItem/check-upc?${q.toString()}`;
  },
  // `Next DD Pseudo UPC` (BTN-13).
  ShipperPseudoUpc: (currentUpc?: number | null) =>
    `${ITEMS_BASE_API_URL}/api/item/ShipperItem/pseudo-upc` +
    (currentUpc == null ? "" : `?currentUpc=${currentUpc}`),
  RetailItemByUpc: (upc: number) =>
    `${ITEMS_BASE_API_URL}/api/item/lookup/ritem-by-upc/${upc}`,
  // ── Regular Pricing screens (PRC_REG_PRC_BNR) ────────────────────
  // priceableOnly restricts results to item types a price can be created for
  // (RITEM_TYP 'R', plus 'M' when searching by UPC) — the legacy promo/price
  // item cursors. Opt-in: Shipper Maint uses this same endpoint to find
  // RITEM_TYP='S' items and must not be filtered.
  ItemSearch: (
    sysCd?: string,
    whitemNo?: number,
    upc?: string,
    includeInactive?: boolean,
    priceableOnly?: boolean,
    // RETAIL_ITEM.RITEM_NO — the legacy ITEMS block's "Retail ID" key. Last in
    // the list so every existing caller is untouched.
    ritemNo?: number
  ) => {
    const params = new URLSearchParams();
    if (sysCd) params.set("sysCd", sysCd);
    if (whitemNo != null) params.set("whitemNo", String(whitemNo));
    if (upc) params.set("upc", upc);
    if (ritemNo != null) params.set("ritemNo", String(ritemNo));
    if (includeInactive) params.set("includeInactive", "true");
    if (priceableOnly) params.set("priceableOnly", "true");
    return `${ITEMS_BASE_API_URL}/api/item/ItemSearch?${params.toString()}`;
  },
  RetailPriceByItem: (ritemNo: number) =>
    `${ITEMS_BASE_API_URL}/api/item/RetailPrice/by-item/${ritemNo}`,
  RetailPriceCreate: `${ITEMS_BASE_API_URL}/api/item/RetailPrice`,
  RetailPriceCreateBulk: `${ITEMS_BASE_API_URL}/api/item/RetailPrice/bulk`,
  RetailPriceCreateAllZones: `${ITEMS_BASE_API_URL}/api/item/RetailPrice/create-all-zones`,
  RetailPriceUpdate: (id: number) =>
    `${ITEMS_BASE_API_URL}/api/item/RetailPrice/${id}`,
  RetailPriceDelete: (id: number) =>
    `${ITEMS_BASE_API_URL}/api/item/RetailPrice/${id}`,
  RetailPriceDeleteBatch: `${ITEMS_BASE_API_URL}/api/item/RetailPrice/delete-batch`,
  RetailPriceGenerateWarnings: `${ITEMS_BASE_API_URL}/api/item/RetailPrice/generate-warnings`,
  RetailPriceWarnings: `${ITEMS_BASE_API_URL}/api/item/RetailPriceWarning`,
  // ── Promotional Pricing (PRC_ADD_PNDG_PROMO / prc_promo_prc_bnr) ──
  // Promotional RETAIL_PRICE rows (PROM_CD not null, PRC_TYP_IND 'PPR'). The
  // grid itself is read through RetailPriceByItem above; these endpoints cover
  // the promo-specific lookups plus the bulk "apply promotion" insert.
  // MAINT-FORM-92. NEW operations -- they must be declared in the APIM spec
  // before they answer in a deployed environment.
  PromoGroups: `${ITEMS_BASE_API_URL}/api/item/PromoPrice/promo-groups`,
  PromoGroupStores: (
    promoGrpId: number,
    bnrCd: string,
    promCd: number,
    prcGrpCd: number,
    splrNo?: number | null
  ) => {
    let url = `${ITEMS_BASE_API_URL}/api/item/PromoPrice/promo-group-stores?promoGrpId=${promoGrpId}&bnrCd=${encodeURIComponent(bnrCd)}&promCd=${promCd}&prcGrpCd=${prcGrpCd}`;
    if (splrNo != null) url += `&splrNo=${splrNo}`;
    return url;
  },
  PromoPriceCodes: (bnrCd: string) =>
    `${ITEMS_BASE_API_URL}/api/item/PromoPrice/promo-codes?bnrCd=${encodeURIComponent(bnrCd)}`,
  PromoPriceAdvertisedItems: `${ITEMS_BASE_API_URL}/api/item/PromoPrice/advertised-items`,
  PromoPriceDefaultDates: (bnrCd: string, promCd: number) =>
    `${ITEMS_BASE_API_URL}/api/item/PromoPrice/default-dates?bnrCd=${encodeURIComponent(bnrCd)}&promCd=${promCd}`,
  // ADD-FORM-017: the default state of the Scanbacks checkbox. Resolved
  // server-side because it is a two-step lookup (BANNER, then the item's
  // CATEGORY) whose second step legacy treated as a hard error when missing.
  PromoPriceScanbackDefault: (ritemNo: number, bnrCd: string) =>
    `${ITEMS_BASE_API_URL}/api/item/PromoPrice/scanback-default?ritemNo=${ritemNo}&bnrCd=${encodeURIComponent(bnrCd)}`,
  // HELPERS-20. The price a percent or amount off actually produces, for the
  // zone/store the row will be created against. Legacy showed this the moment
  // the value was typed (CALC_PRICE_DISPLAY -> :retail_price.prom_pct_calc);
  // the new screen only showed it in the grid AFTER saving, which is the wrong
  // side of the decision.
  PromoPriceCalculated: (q: {
    ritemNo: number;
    bnrCd: string;
    znNo: number;
    strtDte: string;
    promCd: number;
    strNo?: number | null;
    ovrdNo?: number | null;
    splrNo?: number | null;
    promPct?: number | null;
    promAmtOff?: number | null;
  }) => {
    const p = new URLSearchParams({
      ritemNo: String(q.ritemNo),
      bnrCd: q.bnrCd,
      znNo: String(q.znNo),
      strtDte: q.strtDte,
      promCd: String(q.promCd),
    });
    if (q.strNo != null) p.set("strNo", String(q.strNo));
    if (q.ovrdNo != null) p.set("ovrdNo", String(q.ovrdNo));
    if (q.splrNo != null) p.set("splrNo", String(q.splrNo));
    if (q.promPct != null) p.set("promPct", String(q.promPct));
    if (q.promAmtOff != null) p.set("promAmtOff", String(q.promAmtOff));
    return `${ITEMS_BASE_API_URL}/api/item/PromoPrice/calculated-price?${p.toString()}`;
  },
  PromoPriceApply: `${ITEMS_BASE_API_URL}/api/item/PromoPrice/apply`,
  PromoPriceDeleteBatch: `${ITEMS_BASE_API_URL}/api/item/PromoPrice/delete-batch`,
  // skipWarnings mirrors the create path's two-phase gate: the first DELETE
  // returns del_promo_warnings without removing anything, and the user
  // acknowledges before the second call actually deletes.
  // warningsOnly reports what a delete WOULD warn about without deleting, so a
  // bulk delete can show one dialog for the whole batch before anything goes.
  PromoPriceById: (
    id: number,
    skipWarnings?: boolean,
    warningsOnly?: boolean
  ) => {
    const q = warningsOnly
      ? "?warningsOnly=true"
      : skipWarnings
        ? "?skipWarnings=true"
        : "";
    return `${ITEMS_BASE_API_URL}/api/item/PromoPrice/${id}${q}`;
  },
  PriceReasonCodes: `${ITEMS_BASE_API_URL}/api/item/PriceReasonCode?page=1&limit=1000`,
  PricingSuppliersByItem: (ritemNo: number) =>
    `${ITEMS_BASE_API_URL}/api/item/PricingLookup/suppliers-for-item/${ritemNo}`,
  AddPendingBanners: `${ITEMS_BASE_API_URL}/api/item/AddPendingPrice/banners`,
  AddPendingZoneOverrides: (
    bnrCd: string,
    prcGrpCd: number,
    splrNo?: number
  ) => {
    let url = `${ITEMS_BASE_API_URL}/api/item/AddPendingPrice/zone-overrides?bnrCd=${bnrCd}&prcGrpCd=${prcGrpCd}`;
    if (splrNo != null) url += `&splrNo=${splrNo}`;
    return url;
  },
  AddPendingEffectiveDate: (bnrCd: string) =>
    `${ITEMS_BASE_API_URL}/api/item/AddPendingPrice/effective-date?bnrCd=${bnrCd}`,
  AddPendingItemInfo: (params: {
    ritemNo?: number;
    bnrCd: string;
    prcGrpCd?: number;
    znNo?: number;
    ovrdNo?: number;
    splrNo?: number;
    sysCd?: string;
    whitemNo?: number;
    upc?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params.ritemNo != null)
      searchParams.set("ritemNo", String(params.ritemNo));
    searchParams.set("bnrCd", params.bnrCd);
    if (params.prcGrpCd != null)
      searchParams.set("prcGrpCd", String(params.prcGrpCd));
    if (params.znNo != null) searchParams.set("znNo", String(params.znNo));
    if (params.ovrdNo != null)
      searchParams.set("ovrdNo", String(params.ovrdNo));
    if (params.splrNo != null)
      searchParams.set("splrNo", String(params.splrNo));
    if (params.sysCd) searchParams.set("sysCd", params.sysCd);
    if (params.whitemNo != null)
      searchParams.set("whitemNo", String(params.whitemNo));
    if (params.upc != null) searchParams.set("upc", String(params.upc));
    return `${ITEMS_BASE_API_URL}/api/item/AddPendingPrice/item-info?${searchParams.toString()}`;
  },
  PricingZones: (bnrCd: string, prcGrpCd?: number) => {
    const params = new URLSearchParams();
    params.set("bnrCd", bnrCd);
    if (prcGrpCd != null) params.set("prcGrpCd", String(prcGrpCd));
    return `${ITEMS_BASE_API_URL}/api/item/PricingLookup/zones?${params.toString()}`;
  },
  PricingOverrides: (znNo: number, bnrCd: string, prcGrpCd?: number) => {
    const params = new URLSearchParams();
    params.set("znNo", String(znNo));
    params.set("bnrCd", bnrCd);
    if (prcGrpCd != null) params.set("prcGrpCd", String(prcGrpCd));
    return `${ITEMS_BASE_API_URL}/api/item/PricingLookup/overrides?${params.toString()}`;
  },
  // znNo is optional: the endpoint treats it as a filter, and Display Price
  // Details allows a store-only query with no zone chosen yet. Existing callers
  // always pass a number, so omitting it when absent changes nothing for them.
  PricingStores: (znNo: number | null | undefined, bnrCd: string, ovrdNo?: number) => {
    const params = new URLSearchParams();
    if (znNo != null) params.set("znNo", String(znNo));
    params.set("bnrCd", bnrCd);
    if (ovrdNo != null) params.set("ovrdNo", String(ovrdNo));
    return `${ITEMS_BASE_API_URL}/api/item/PricingLookup/stores?${params.toString()}`;
  },
  // Full, supplier-scoped Banner>Zone>Store structure in ONE call
  // (SYSTEM_ZONE_SUPPLIER_CUST): [{ zoneNumber, priceGroupCode, stores: [] }].
  // Omit splrNo for the warehouse structure. Replaces the old zones + per-zone
  // stores fetches that mis-mapped stores across zones.
  // Controlling regular price group for an item in a banner (legacy
  // RP_GET_PRC_GRP_CD): the alternate price group when the banner allows it,
  // else the item's default. Returns { priceGroupCode }.
  PriceGroupForItem: (bnrCd: string, ritemNo: number, whitemNo?: number) =>
    `${ITEMS_BASE_API_URL}/api/item/PricingLookup/price-group?bnrCd=${encodeURIComponent(bnrCd)}&ritemNo=${ritemNo}` +
    (whitemNo != null ? `&whitemNo=${whitemNo}` : ""),
  PricingZoneStructure: (bnrCd: string, splrNo?: number, prcGrpCd?: number) => {
    const params = new URLSearchParams();
    params.set("bnrCd", bnrCd);
    if (splrNo != null) params.set("splrNo", String(splrNo));
    // Required for the warehouse (null-supplier) set, where one zone spans
    // many price groups; omit for a DSD supplier (rows are already PG 5).
    if (prcGrpCd != null) params.set("prcGrpCd", String(prcGrpCd));
    return `${ITEMS_BASE_API_URL}/api/item/PricingLookup/zone-structure?${params.toString()}`;
  },
  AddPendingApplyToZone: `${ITEMS_BASE_API_URL}/api/item/AddPendingPrice/apply-to-zone`,
  AddPendingWarnings: (ids: number[]) =>
    `${ITEMS_BASE_API_URL}/api/item/AddPendingPrice/warnings?ids=${ids.join(",")}`,
  PromotionMaintList: `${ITEMS_BASE_API_URL}/api/PromotionMaint/list`,
  PromotionMaintCreate: `${ITEMS_BASE_API_URL}/api/PromotionMaint`,
  // Promotion Types / Rules validation-list screen — PRICE_TYPE CRUD keyed
  // by banner (PromotionMaintController.ListPromotionTypes / CreatePromotionType
  // / UpdatePromotionType).
  PromotionTypesList: (bnrCd: string) =>
    `${ITEMS_BASE_API_URL}/api/PromotionMaint/promotion-types?bnrCd=${encodeURIComponent(bnrCd)}`,
  PromotionTypeCreate: `${ITEMS_BASE_API_URL}/api/PromotionMaint/promotion-types`,
  PromotionTypeUpdate: (promCd: number) =>
    `${ITEMS_BASE_API_URL}/api/PromotionMaint/promotion-types/${promCd}`,
  PromotionTypeDelete: (promCd: number) =>
    `${ITEMS_BASE_API_URL}/api/PromotionMaint/promotion-types/${promCd}`,
  PromotionMaint: (promCd: number) =>
    `${ITEMS_BASE_API_URL}/api/PromotionMaint/${promCd}`,
  PromotionMaintItems: (promCd: number) =>
    `${ITEMS_BASE_API_URL}/api/PromotionMaint/${promCd}/items`,
  PromotionMaintItemById: (promCd: number, pritId: number) =>
    `${ITEMS_BASE_API_URL}/api/PromotionMaint/${promCd}/items/${pritId}`,
  PromotionMaintZones: (promCd: number) =>
    `${ITEMS_BASE_API_URL}/api/PromotionMaint/${promCd}/zones`,
  PromotionMaintZoneById: (promCd: number, przId: number) =>
    `${ITEMS_BASE_API_URL}/api/PromotionMaint/${promCd}/zones/${przId}`,
  PromotionMaintPrices: (promCd: number) =>
    `${ITEMS_BASE_API_URL}/api/PromotionMaint/${promCd}/prices`,
  PromotionMaintPriceById: (promCd: number, rprcId: number) =>
    `${ITEMS_BASE_API_URL}/api/PromotionMaint/${promCd}/prices/${rprcId}`,

  // Promotion Benchmark Codes validation list (PromotionBenchmarkController /
  // PROMOTION_BENCHMARK). bmrkCd is the string benchmark code.
  BenchmarkCodesList: `${ITEMS_BASE_API_URL}/api/PromotionBenchmark`,
  BenchmarkCodeCreate: `${ITEMS_BASE_API_URL}/api/PromotionBenchmark`,
  BenchmarkCodeUpdate: (bmrkCd: string) =>
    `${ITEMS_BASE_API_URL}/api/PromotionBenchmark/${encodeURIComponent(bmrkCd)}`,
  BenchmarkCodeDelete: (bmrkCd: string) =>
    `${ITEMS_BASE_API_URL}/api/PromotionBenchmark/${encodeURIComponent(bmrkCd)}`,

  // Promotion Item Types validation list (PromotionItemTypeController /
  // PROMOTION_ITEM_TYPE). promItmCd is the string promotion item type code.
  PromotionItemTypesList: `${ITEMS_BASE_API_URL}/api/PromotionItemType`,
  PromotionItemTypeCreate: `${ITEMS_BASE_API_URL}/api/PromotionItemType`,
  PromotionItemTypeUpdate: (promItmCd: string) =>
    `${ITEMS_BASE_API_URL}/api/PromotionItemType/${encodeURIComponent(promItmCd)}`,
  PromotionItemTypeDelete: (promItmCd: string) =>
    `${ITEMS_BASE_API_URL}/api/PromotionItemType/${encodeURIComponent(promItmCd)}`,

  // Reward Promotion Status validation list (RewardPromotionStatusController /
  // REWARD_PROM_STAT). statCd is the one-char status code.
  RewardPromotionStatusList: `${ITEMS_BASE_API_URL}/api/RewardPromotionStatus`,
  RewardPromotionStatusCreate: `${ITEMS_BASE_API_URL}/api/RewardPromotionStatus`,
  RewardPromotionStatusUpdate: (statCd: string) =>
    `${ITEMS_BASE_API_URL}/api/RewardPromotionStatus/${encodeURIComponent(statCd)}`,
  RewardPromotionStatusDelete: (statCd: string) =>
    `${ITEMS_BASE_API_URL}/api/RewardPromotionStatus/${encodeURIComponent(statCd)}`,

  // Ad Display Plan validation list (AdDisplayPlannerController /
  // AD_DISPLAY_PLANNER). dispPlanId is the numeric plan id.
  AdDisplayPlansList: `${ITEMS_BASE_API_URL}/api/AdDisplayPlanner`,
  AdDisplayPlanCreate: `${ITEMS_BASE_API_URL}/api/AdDisplayPlanner`,
  AdDisplayPlanUpdate: (dispPlanId: number) =>
    `${ITEMS_BASE_API_URL}/api/AdDisplayPlanner/${dispPlanId}`,
  AdDisplayPlanDelete: (dispPlanId: number) =>
    `${ITEMS_BASE_API_URL}/api/AdDisplayPlanner/${dispPlanId}`,

  // Scanback Entry Types validation list (ScanbackEntryTypeController /
  // SCANBACK_ENTRY_TYPE). entryType is the string entry-type code.
  ScanbackEntryTypesList: `${ITEMS_BASE_API_URL}/api/ScanbackEntryType`,
  ScanbackEntryTypeCreate: `${ITEMS_BASE_API_URL}/api/ScanbackEntryType`,
  ScanbackEntryTypeUpdate: (entryType: string) =>
    `${ITEMS_BASE_API_URL}/api/ScanbackEntryType/${encodeURIComponent(entryType)}`,
  ScanbackEntryTypeDelete: (entryType: string) =>
    `${ITEMS_BASE_API_URL}/api/ScanbackEntryType/${encodeURIComponent(entryType)}`,

  // Ad Type validation list (AdTypeController / AD_TYPE). adTypId is the numeric
  // ad type id.
  AdTypesList: `${ITEMS_BASE_API_URL}/api/AdType`,
  AdTypeCreate: `${ITEMS_BASE_API_URL}/api/AdType`,
  AdTypeUpdate: (adTypId: number) =>
    `${ITEMS_BASE_API_URL}/api/AdType/${adTypId}`,
  AdTypeDelete: (adTypId: number) =>
    `${ITEMS_BASE_API_URL}/api/AdType/${adTypId}`,

  // Secondary Promotion Links validation list (SecondaryPromoLinkController /
  // SECONDARY_PROMO_LINKS). Composite key: club / link / perks-link.
  SecondaryPromoLinksList: `${ITEMS_BASE_API_URL}/api/SecondaryPromoLink`,
  SecondaryPromoLinkCreate: `${ITEMS_BASE_API_URL}/api/SecondaryPromoLink`,
  SecondaryPromoLinkUpdate: (clubNo: number, linkNo: number, perksLinkNo: number) =>
    `${ITEMS_BASE_API_URL}/api/SecondaryPromoLink/${clubNo}/${linkNo}/${perksLinkNo}`,
  SecondaryPromoLinkDelete: (clubNo: number, linkNo: number, perksLinkNo: number) =>
    `${ITEMS_BASE_API_URL}/api/SecondaryPromoLink/${clubNo}/${linkNo}/${perksLinkNo}`,

  // Scanback Business Units (SCANBACK_BUSINESS_UNIT). unitNo is the string code.
  ScanbackBusinessUnitsList: `${ITEMS_BASE_API_URL}/api/ScanbackBusinessUnit`,
  ScanbackBusinessUnitCreate: `${ITEMS_BASE_API_URL}/api/ScanbackBusinessUnit`,
  ScanbackBusinessUnitUpdate: (unitNo: string) =>
    `${ITEMS_BASE_API_URL}/api/ScanbackBusinessUnit/${encodeURIComponent(unitNo)}`,
  ScanbackBusinessUnitDelete: (unitNo: string) =>
    `${ITEMS_BASE_API_URL}/api/ScanbackBusinessUnit/${encodeURIComponent(unitNo)}`,

  // Advertised Item Codes (ADVERTISED_ITEM). advItmCd is the string code.
  AdvertisedItemsList: `${ITEMS_BASE_API_URL}/api/AdvertisedItem`,
  AdvertisedItemCreate: `${ITEMS_BASE_API_URL}/api/AdvertisedItem`,
  AdvertisedItemUpdate: (advItmCd: string) =>
    `${ITEMS_BASE_API_URL}/api/AdvertisedItem/${encodeURIComponent(advItmCd)}`,
  AdvertisedItemDelete: (advItmCd: string) =>
    `${ITEMS_BASE_API_URL}/api/AdvertisedItem/${encodeURIComponent(advItmCd)}`,

  // Promotion Action Codes (PROMOTION_ACTION). promActnCd is the string code.
  PromotionActionsList: `${ITEMS_BASE_API_URL}/api/PromotionAction`,
  PromotionActionCreate: `${ITEMS_BASE_API_URL}/api/PromotionAction`,
  PromotionActionUpdate: (promActnCd: string) =>
    `${ITEMS_BASE_API_URL}/api/PromotionAction/${encodeURIComponent(promActnCd)}`,
  PromotionActionDelete: (promActnCd: string) =>
    `${ITEMS_BASE_API_URL}/api/PromotionAction/${encodeURIComponent(promActnCd)}`,

  // Distribution Method (AD_DISTRIBUTION_METHOD). adDistId is the numeric id.
  AdDistributionMethodsList: `${ITEMS_BASE_API_URL}/api/AdDistributionMethod`,
  AdDistributionMethodCreate: `${ITEMS_BASE_API_URL}/api/AdDistributionMethod`,
  AdDistributionMethodUpdate: (adDistId: number) =>
    `${ITEMS_BASE_API_URL}/api/AdDistributionMethod/${adDistId}`,
  AdDistributionMethodDelete: (adDistId: number) =>
    `${ITEMS_BASE_API_URL}/api/AdDistributionMethod/${adDistId}`,

  // Ad Location (AD_LOCATION). adLocnId is the numeric id.
  AdLocationsList: `${ITEMS_BASE_API_URL}/api/AdLocation`,
  AdLocationCreate: `${ITEMS_BASE_API_URL}/api/AdLocation`,
  AdLocationUpdate: (adLocnId: number) =>
    `${ITEMS_BASE_API_URL}/api/AdLocation/${adLocnId}`,
  AdLocationDelete: (adLocnId: number) =>
    `${ITEMS_BASE_API_URL}/api/AdLocation/${adLocnId}`,

  // ── Item Pricing validation lists (Pricing ▸ Item Pricing) ──────────
  // Rule Operation Codes (RULE_OPERATION). Key is the string code.
  RuleOperationsList: `${ITEMS_BASE_API_URL}/api/RuleOperation`,
  RuleOperationCreate: `${ITEMS_BASE_API_URL}/api/RuleOperation`,
  RuleOperationUpdate: (code: string) =>
    `${ITEMS_BASE_API_URL}/api/RuleOperation/${encodeURIComponent(code)}`,
  RuleOperationDelete: (code: string) =>
    `${ITEMS_BASE_API_URL}/api/RuleOperation/${encodeURIComponent(code)}`,

  // Rule Base Codes (RULE_BASE).
  RuleBasesList: `${ITEMS_BASE_API_URL}/api/RuleBase`,
  RuleBaseCreate: `${ITEMS_BASE_API_URL}/api/RuleBase`,
  RuleBaseUpdate: (code: string) =>
    `${ITEMS_BASE_API_URL}/api/RuleBase/${encodeURIComponent(code)}`,
  RuleBaseDelete: (code: string) =>
    `${ITEMS_BASE_API_URL}/api/RuleBase/${encodeURIComponent(code)}`,

  // Rule Target Codes (RULE_TARGET).
  RuleTargetsList: `${ITEMS_BASE_API_URL}/api/RuleTarget`,
  RuleTargetCreate: `${ITEMS_BASE_API_URL}/api/RuleTarget`,
  RuleTargetUpdate: (code: string) =>
    `${ITEMS_BASE_API_URL}/api/RuleTarget/${encodeURIComponent(code)}`,
  RuleTargetDelete: (code: string) =>
    `${ITEMS_BASE_API_URL}/api/RuleTarget/${encodeURIComponent(code)}`,

  // Competitor Banners (COMPETITOR_BANNER).
  CompetitorBannersList: `${ITEMS_BASE_API_URL}/api/CompetitorBanner`,
  CompetitorBannerCreate: `${ITEMS_BASE_API_URL}/api/CompetitorBanner`,
  CompetitorBannerUpdate: (code: string) =>
    `${ITEMS_BASE_API_URL}/api/CompetitorBanner/${encodeURIComponent(code)}`,
  CompetitorBannerDelete: (code: string) =>
    `${ITEMS_BASE_API_URL}/api/CompetitorBanner/${encodeURIComponent(code)}`,

  // Price Reason Codes (PRICE_REASON_CODE) — maintenance endpoint.
  PriceReasonCodeMaintList: `${ITEMS_BASE_API_URL}/api/PriceReasonCodeMaint`,
  PriceReasonCodeMaintCreate: `${ITEMS_BASE_API_URL}/api/PriceReasonCodeMaint`,
  PriceReasonCodeMaintUpdate: (code: string) =>
    `${ITEMS_BASE_API_URL}/api/PriceReasonCodeMaint/${encodeURIComponent(code)}`,
  PriceReasonCodeMaintDelete: (code: string) =>
    `${ITEMS_BASE_API_URL}/api/PriceReasonCodeMaint/${encodeURIComponent(code)}`,

  // Price Book Codes (PRICE_BOOK_CODE) — Items-API maintenance endpoint.
  PriceBookCodeMaintList: `${ITEMS_BASE_API_URL}/api/PriceBookCodeMaint`,
  PriceBookCodeMaintCreate: `${ITEMS_BASE_API_URL}/api/PriceBookCodeMaint`,
  PriceBookCodeMaintUpdate: (code: string) =>
    `${ITEMS_BASE_API_URL}/api/PriceBookCodeMaint/${encodeURIComponent(code)}`,
  PriceBookCodeMaintDelete: (code: string) =>
    `${ITEMS_BASE_API_URL}/api/PriceBookCodeMaint/${encodeURIComponent(code)}`,

  // Price Basis (PRICE_BASIS) — code + description + value.
  PriceBasisList: `${ITEMS_BASE_API_URL}/api/PriceBasis`,
  PriceBasisCreate: `${ITEMS_BASE_API_URL}/api/PriceBasis`,
  PriceBasisUpdate: (code: string) =>
    `${ITEMS_BASE_API_URL}/api/PriceBasis/${encodeURIComponent(code)}`,
  PriceBasisDelete: (code: string) =>
    `${ITEMS_BASE_API_URL}/api/PriceBasis/${encodeURIComponent(code)}`,

  // Price Status Codes (PRICE_STATUS) — code + description + order.
  PriceStatusList: `${ITEMS_BASE_API_URL}/api/PriceStatus`,
  PriceStatusCreate: `${ITEMS_BASE_API_URL}/api/PriceStatus`,
  PriceStatusUpdate: (code: string) =>
    `${ITEMS_BASE_API_URL}/api/PriceStatus/${encodeURIComponent(code)}`,
  PriceStatusDelete: (code: string) =>
    `${ITEMS_BASE_API_URL}/api/PriceStatus/${encodeURIComponent(code)}`,

  // Account Number / Promotion Xref (ACCOUNT_PROMOTION) — composite key.
  AccountPromotionList: `${ITEMS_BASE_API_URL}/api/AccountPromotion?page=1&limit=1000`,
  AccountPromotionCreate: `${ITEMS_BASE_API_URL}/api/AccountPromotion`,
  AccountPromotionDelete: (natActNo: string, advItmCd: string, promCd: number) =>
    `${ITEMS_BASE_API_URL}/api/AccountPromotion/${encodeURIComponent(natActNo)}/${encodeURIComponent(advItmCd)}/${promCd}`,

  // Maintain EDLP Delete Date (EDLP_STORE_ITEM) — query + bulk date stamp.
  EdlpDeleteDateQuery: (params: string) =>
    `${ITEMS_BASE_API_URL}/api/EdlpDeleteDate?${params}`,
  EdlpDeleteDateBulk: `${ITEMS_BASE_API_URL}/api/EdlpDeleteDate/bulk`,

  // Ad Price Format (AD_PRICE_FORMAT). prcFrmtId is the numeric id.
  AdPriceFormatsList: `${ITEMS_BASE_API_URL}/api/AdPriceFormat`,
  AdPriceFormatCreate: `${ITEMS_BASE_API_URL}/api/AdPriceFormat`,
  AdPriceFormatUpdate: (prcFrmtId: number) =>
    `${ITEMS_BASE_API_URL}/api/AdPriceFormat/${prcFrmtId}`,
  AdPriceFormatDelete: (prcFrmtId: number) =>
    `${ITEMS_BASE_API_URL}/api/AdPriceFormat/${prcFrmtId}`,

  // Promotion Groups validation list (PromoGroupController / PROMO_GROUP +
  // PROMO_GRP_STR store membership). promoGrpId is the numeric group id.
  PromoGroupsList: `${ITEMS_BASE_API_URL}/api/PromoGroup`,
  PromoGroupCreate: `${ITEMS_BASE_API_URL}/api/PromoGroup`,
  PromoGroupUpdate: (promoGrpId: number) =>
    `${ITEMS_BASE_API_URL}/api/PromoGroup/${promoGrpId}`,
  PromoGroupCopy: (promoGrpId: number) =>
    `${ITEMS_BASE_API_URL}/api/PromoGroup/${promoGrpId}/copy`,
  PromoGroupDelete: (promoGrpId: number) =>
    `${ITEMS_BASE_API_URL}/api/PromoGroup/${promoGrpId}`,
  PromoGroupMemberStores: (promoGrpId: number) =>
    `${ITEMS_BASE_API_URL}/api/PromoGroup/${promoGrpId}/stores`,
  PromoGroupMemberStoreDelete: (promoGrpId: number, storeNumber: number) =>
    `${ITEMS_BASE_API_URL}/api/PromoGroup/${promoGrpId}/stores/${storeNumber}`,
  PromoGroupZoneBanners: `${ITEMS_BASE_API_URL}/api/PromoGroup/zone-banners`,
  PromoGroupZoneStores: (bnrCd: string) =>
    `${ITEMS_BASE_API_URL}/api/PromoGroup/zone-stores?bnrCd=${encodeURIComponent(bnrCd)}`,
  PromoGroupBulkAddStores: (promoGrpId: number) =>
    `${ITEMS_BASE_API_URL}/api/PromoGroup/${promoGrpId}/stores/bulk`,
  PromoGroupBulkRemoveStores: (promoGrpId: number) =>
    `${ITEMS_BASE_API_URL}/api/PromoGroup/${promoGrpId}/stores/bulk-remove`,

  // In-app feedback (dev/staging only via Feedback feature flag).
  UserFeedback: `${ITEMS_BASE_API_URL}/api/utility/UserFeedback`,

  // ItemMaintenance — re-added for recovery PR. These endpoints are
  // served by the BE Items + Cost APIs (verified against the swagger
  // in apimartifacts/apis/b2b-ge-merch-rp-items + b2b-ge-merch-rp-cost)
  // but were pruned from this file by earlier cleanups when no other
  // screen referenced them.
  AddBanner: (id: number) =>
    `${ITEMS_BASE_API_URL}/api/item/RetailItem/${id}/add-banner`,
  AddPendingPrices: (id: number) =>
    `${ITEMS_BASE_API_URL}/api/item/RetailItem/${id}/add-pending-prices`,
  BannerPermissions: `${ITEMS_BASE_API_URL}/api/item/RetailItem/banner-permissions`,
  BannerRemovalImpact: (id: number, bannerCode: string) =>
    `${ITEMS_BASE_API_URL}/api/item/RetailItem/${id}/banner-removal-impact/${bannerCode}`,
  BannersForSupplier: (ritemNo: number, supplierNumber: number) =>
    `${ITEMS_BASE_API_URL}/api/item/lookup/banners-for-supplier/${ritemNo}/${supplierNumber}`,
  EnsureAuths: (id: number) =>
    `${ITEMS_BASE_API_URL}/api/item/RetailItem/${id}/ensure-auths`,
  // Legacy ITM_ITEM_SAGS — an item's store authorization groups. Distinct from
  // ItemBanners, which answers the coarser "is the item in this banner".
  RetailItemSags: (ritemNo: number) =>
    `${ITEMS_BASE_API_URL}/api/item/RetailItem/${ritemNo}/sags`,
  ItemBanners: (id: number) =>
    `${ITEMS_BASE_API_URL}/api/item/RetailItem/${id}/banners`,
  ItemCostZoneCostCopyByUpc: `${COST_BASE_API_URL}/api/cost/ItemCostZoneCost/copy-by-upc`,
  ItemImage: (id: number) =>
    `${ITEMS_BASE_API_URL}/api/item/RetailItem/${id}/image`,
  LookupCategoryManagers: `${ITEMS_BASE_API_URL}/api/item/lookup/categorymanagers`,
  LookupHbUom: `${ITEMS_BASE_API_URL}/api/item/lookup/hbuom`,
  LookupIfpsPlus: `${ITEMS_BASE_API_URL}/api/item/lookup/ifpsplus`,
  LookupItemAttributes: `${ITEMS_BASE_API_URL}/api/item/lookup/itemattributes`,
  LookupNextRitemNo: `${ITEMS_BASE_API_URL}/api/item/lookup/nextritemno`,
  LookupPosTareCodes: `${ITEMS_BASE_API_URL}/api/item/lookup/postarecodes`,
  LookupPriceBasis: `${ITEMS_BASE_API_URL}/api/item/lookup/pricebasis`,
  LookupPriceGroups: `${ITEMS_BASE_API_URL}/api/item/lookup/pricegroups`,
  LookupPriceGroupsForItem: (ritemNo: number) =>
    `${ITEMS_BASE_API_URL}/api/item/lookup/pricegroups/${ritemNo}`,
  LookupPriceZones: (supplierNumber: number, supplierType: number) =>
    `${ITEMS_BASE_API_URL}/api/item/lookup/pricezones?supplierNumber=${supplierNumber}&supplierType=${supplierType}`,
  PriceGroupMovePending: `${ITEMS_BASE_API_URL}/api/item/PriceGroupMove/pending`,
  PriceGroupMovePendingById: (prcmId: number) =>
    `${ITEMS_BASE_API_URL}/api/item/PriceGroupMove/pending/${prcmId}`,
  PriceGroupMovePreview: `${ITEMS_BASE_API_URL}/api/item/PriceGroupMove/preview`,
  PriceGroupMoveRequest: `${ITEMS_BASE_API_URL}/api/item/PriceGroupMove/request`,
  PriceGroupMoveProcess: `${ITEMS_BASE_API_URL}/api/item/PriceGroupMove/process`,
  RetailItemDefaultBanners: `${ITEMS_BASE_API_URL}/api/item/RetailItem/banners/defaults`,
  PseudoUpcRanges: `${ITEMS_BASE_API_URL}/api/item/PseudoUpc/ranges`,
  PseudoUpcIssue: `${ITEMS_BASE_API_URL}/api/item/PseudoUpc/issue`,
  LookupPseudoRanges: `${ITEMS_BASE_API_URL}/api/item/lookup/pseudoranges`,
  LookupSupplierBanners: (ritemNo: number) =>
    `${ITEMS_BASE_API_URL}/api/item/lookup/supplier-banners/${ritemNo}`,
  LookupSuppliers: `${ITEMS_BASE_API_URL}/api/item/lookup/suppliers`,
  LookupSystemCodes: `${ITEMS_BASE_API_URL}/api/item/lookup/systemcodes`,
  // The two wholesale pickers. The supplier list is filtered by the wholesale
  // banner already entered, so only resolvable supplier/banner pairs are
  // offered - which is what the legacy WHSL_SPLR_LOV does.
  /** Which database this session is on — legacy shows it in the toolbar. */
  LookupDatabaseName: `${ITEMS_BASE_API_URL}/api/item/lookup/database-name`,
  /** SUPPLIER with the columns the legacy SUPPLIER_LOV lists, type-then-number. */
  LookupSupplierLov: (filter?: string) =>
    `${ITEMS_BASE_API_URL}/api/item/lookup/supplier-lov${
      filter ? `?filter=${encodeURIComponent(filter)}` : ""
    }`,
  /**
   * STORE with the columns the legacy STORE_LOV lists, ordered by number.
   *
   * Distinct from PricingStores, which is the banner/zone-scoped list other
   * screens use: the legacy record group has no predicate at all.
   */
  LookupStoreLov: (filter?: string) =>
    `${ITEMS_BASE_API_URL}/api/item/lookup/store-lov${
      filter ? `?filter=${encodeURIComponent(filter)}` : ""
    }`,
  LookupWholesaleBanners: `${ITEMS_BASE_API_URL}/api/item/lookup/wholesale-banners`,
  LookupWholesaleSuppliers: (bnrCd: string) =>
    `${ITEMS_BASE_API_URL}/api/item/lookup/wholesale-suppliers?bnrCd=${encodeURIComponent(bnrCd)}`,
  LookupTareCodes: `${ITEMS_BASE_API_URL}/api/item/lookup/tarecodes`,
  LookupUom: `${ITEMS_BASE_API_URL}/api/item/lookup/uom`,
  PriceGroupMove: (id: number) =>
    `${ITEMS_BASE_API_URL}/api/item/RetailItem/${id}/move-price-group`,
  RemoveBanner: (id: number) =>
    `${ITEMS_BASE_API_URL}/api/item/RetailItem/${id}/remove-banner`,
  RetailItemAttributeCreate: `${ITEMS_BASE_API_URL}/api/item/RetailItemAttribute`,
  RetailItemAttributeDelete: (ritemNo: number, itmAtrId: number) =>
    `${ITEMS_BASE_API_URL}/api/item/RetailItemAttribute/${ritemNo}/${itmAtrId}`,
  RetailItemAttributes: (ritemNo: number) =>
    `${ITEMS_BASE_API_URL}/api/item/RetailItemAttribute/${ritemNo}`,
  SupplierItemAddUpc: `${ITEMS_BASE_API_URL}/api/item/SupplierItem/add-upc`,
  SupplierItemById: (id: number) =>
    `${ITEMS_BASE_API_URL}/api/item/SupplierItem/${id}`,
  SupplierItemDelete: (id: number) =>
    `${ITEMS_BASE_API_URL}/api/item/SupplierItem/${id}`,
  SupplierItemDeleteUpc: `${ITEMS_BASE_API_URL}/api/item/SupplierItem/delete-upc`,
  SupplierItemSearch: (params: string) =>
    `${ITEMS_BASE_API_URL}/api/item/SupplierItem/search?${params}`,
  SupplierItemsByRitem: (ritemNo: number) =>
    `${ITEMS_BASE_API_URL}/api/item/SupplierItem/by-ritem/${ritemNo}`,
  UpcByItem: (ritemNumber: number) =>
    `${ITEMS_BASE_API_URL}/api/item/Upc/by-item/${ritemNumber}`,
  // BE renamed /api/item/newitem -> /api/item/NewItem (PascalCase resource).
  NewItemList: `${ITEMS_BASE_API_URL}/api/item/NewItem?limit=500`,
};

export const ERROR_TOAST_MESSAGE =
  "The Retail Pricing Portal is currently unavailable. Please contact the Technology Service Desk.";

export const ERROR_403_MESSAGE = "You don’t have access to this feature.";

export const WRAP_TABLE_HEADER = "wrap-header";

export const INPUT_FIELD_TYPES_ENUM = {
  text: "TEXT",
  dropdown: "DROPDOWN",
  controllerDropdown: "CONTROLLER_DROPDOWN",
  staticDropdown: "STATIC_DROPDOWN",
  datePicker: "DATE_PICKER",
  checkbox: "CHECKBOX",
  radio: "RADIO",
  textarea: "TEXTAREA",
  maskedInput: "MASKED_INPUT",
  hiddenField: "HIDDEN_FIELD",
  typeahead: "TYPEAHEAD",
  numberInput: "NUMBER_INPUT",
};

export const defaultUserClaims = {
  RpUserName: "",
  RP_USER_TYPE: "",
  aio: "",
  aud: "",
  azp: "",
  exp: 0,
  iat: 0,
  iss: "",
  nbf: 0,
  oid: "",
  sub: "",
  tid: "",
  uti: "",
  ver: "",
  claims: [],
};

// User role/claim constants for authorization checks
export const USER_CLAIMS = {
  // Claim name for users authorized to change R->Y on scale items with ingredients
  // This maps to the Oracle Forms g_ingredient_admin_fg global variable
  INGREDIENT_ADMIN: "IngredientAdmin",
  // Claim name for users authorized to apply LP/NLP (low-price / new-low-price)
  // flags on regular price records. Maps to the Oracle Forms LP/NLP gate.
  PRICE_REDUCTION_ADMIN: "PriceReductionAdmin",
};

// Raw RP_USER_ROLES.ROLE_NAME values as returned by /User/Profile's roles[].
// These are the DB role names (SCREAMING_SNAKE) — distinct from the Azure
// claim names in USER_CLAIMS above. Use these to gate on the user's roles.
export const USER_ROLES = {
  PRICE_REDUCTION_ADMIN: "PRICE_REDUCTION_ADMIN",
};

const STORE_INTERNAL_ROUTE_PATHS = {
  storeZones: "/stores/store-zones",
  storeSuppliers: "/stores/store-suppliers",
} as const;

// Allowed internal routes that should not trigger the unsaved changes dialog
export const ALLOWED_INTERNAL_ROUTES = [
  STORE_INTERNAL_ROUTE_PATHS.storeZones,
  STORE_INTERNAL_ROUTE_PATHS.storeSuppliers,
];
